import { useEffect, useMemo, useState } from 'react';
import {
  banPlatformAccount,
  banPlatformIdentity,
  deletePlatformAccount,
  grantScopedPlatformAdmin,
  listScopedPlatformAdmins,
  loadPlatformModerationHistory,
  revokeScopedPlatformAdmin,
  searchPlatformAccounts,
  unbanPlatformAccount,
  unbanPlatformIdentity,
} from '../api/platformAccountModerationApi.js';

const COMMUNITY_SCOPE_LABELS = {
  gallery: '갤러리(갤길) 관리자',
  channel: '채널(챈길) 관리자',
};

const IDENTITY_LABELS = {
  user_id: '계정 UUID',
  dc_gallog: 'DC 식별코드',
  arca: '아카라이브',
  game_openid: '게임 UID',
};

const ACTION_LABELS = {
  ban: '밴',
  unban: '밴 해제',
  delete: '계정 삭제',
};

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '-';
  }
}

function accountTitle(account) {
  return account.gameProfile?.nickname
    || account.profile?.display_name
    || account.dcGallog?.gallog_id
    || account.arca?.display_name
    || account.userId;
}

// 이력에서 알아봐야 하는 건 "어떤 식별자를 막았는가"다. 서버가 identity_snapshot 으로 그 목록을
// 돌려주는데 화면에서는 버리고 계정 UUID만 보여주고 있었다. UUID는 그대로 두면 검색도 안 되고
// 식별자 밴(대상 계정 없음)은 전부 '직접 식별자' 한 줄로 같아 보였다.
function moderationTargetLabel(item) {
  const identities = Array.isArray(item?.identity_snapshot) ? item.identity_snapshot : [];
  const labels = [];
  for (const identity of identities) {
    const value = String(identity?.value || '').trim();
    if (!value || identity?.type === 'user_id') continue;
    labels.push(`${IDENTITY_LABELS[identity?.type] || identity?.type} ${value}`);
  }
  if (labels.length) return labels.join(' · ');
  return String(item?.target_user_id || '').trim() || '대상 정보 없음';
}

function defaultAccountForm() {
  return { reason: '', confirmation: '', preserveBan: true };
}

export function PlatformAccountModerationBoard({ mobile = false }) {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [matchingBans, setMatchingBans] = useState([]);
  const [history, setHistory] = useState([]);
  const [forms, setForms] = useState({});
  const [manual, setManual] = useState({ identityType: 'dc_gallog', identityValue: '', reason: '' });
  const [unbanReason, setUnbanReason] = useState('관리자 확인 후 해제');
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [scopedAdmins, setScopedAdmins] = useState([]);
  const [scopeSelections, setScopeSelections] = useState({});

  const activeMatchingBans = useMemo(
    () => matchingBans.filter((ban) => ban.active),
    [matchingBans],
  );

  const refreshHistory = async () => {
    try {
      const result = await loadPlatformModerationHistory();
      setHistory(result.history || []);
    } catch {
      setHistory([]);
    }
  };

  const refreshScopedAdmins = async () => {
    try {
      const result = await listScopedPlatformAdmins();
      setScopedAdmins(result.admins || []);
    } catch {
      setScopedAdmins([]);
    }
  };

  useEffect(() => {
    refreshHistory();
    refreshScopedAdmins();
  }, []);

  const performSearch = async (event) => {
    event?.preventDefault();
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setNotice({ type: 'error', message: '검색어를 2자 이상 입력해주세요.' });
      return;
    }

    setLoading(true);
    setNotice({ type: '', message: '' });
    try {
      const result = await searchPlatformAccounts(normalizedQuery);
      setAccounts(result.accounts || []);
      setMatchingBans(result.matchingBans || []);
      setSearched(true);
      setForms((current) => {
        const next = { ...current };
        for (const account of result.accounts || []) {
          if (!next[account.userId]) next[account.userId] = defaultAccountForm();
        }
        return next;
      });
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '계정 검색에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrent = async () => {
    await Promise.all([performSearch(), refreshHistory()]);
  };

  const updateForm = (userId, field, value) => {
    setForms((current) => ({
      ...current,
      [userId]: {
        ...(current[userId] || defaultAccountForm()),
        [field]: value,
      },
    }));
  };

  const runAccountAction = async (account, action) => {
    const form = forms[account.userId] || defaultAccountForm();
    const key = `${action}:${account.userId}`;
    setWorking(key);
    setNotice({ type: '', message: '' });

    try {
      if (action === 'ban') {
        await banPlatformAccount({
          targetUserId: account.userId,
          reason: form.reason,
          confirmation: form.confirmation,
        });
        setNotice({ type: 'success', message: `${accountTitle(account)} 계정과 연결 식별자를 밴했습니다.` });
      } else if (action === 'unban') {
        await unbanPlatformAccount({
          targetUserId: account.userId,
          reason: form.reason,
        });
        setNotice({ type: 'success', message: `${accountTitle(account)} 계정의 밴을 해제했습니다.` });
      } else if (action === 'delete') {
        await deletePlatformAccount({
          targetUserId: account.userId,
          reason: form.reason,
          confirmation: form.confirmation,
          preserveBan: form.preserveBan,
        });
        setNotice({
          type: 'success',
          message: `${accountTitle(account)} 계정을 완전히 삭제했습니다.${form.preserveBan ? ' 연결 식별자는 재가입 차단 상태로 유지됩니다.' : ''}`,
        });
      }
      await refreshCurrent();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '계정 처리에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const runManualBan = async (event) => {
    event.preventDefault();
    setWorking('manual-ban');
    setNotice({ type: '', message: '' });
    try {
      await banPlatformIdentity(manual);
      setNotice({ type: 'success', message: '입력한 식별자를 밴했습니다.' });
      setManual((current) => ({ ...current, identityValue: '', reason: '' }));
      await refreshHistory();
      if (query.trim().length >= 2) await performSearch();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '식별자 밴에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const runGrantScopedAdmin = async (account) => {
    const scope = scopeSelections[account.userId] || 'gallery';
    const key = `grant:${account.userId}`;
    setWorking(key);
    setNotice({ type: '', message: '' });
    try {
      await grantScopedPlatformAdmin({ targetUserId: account.userId, scope });
      setNotice({ type: 'success', message: `${accountTitle(account)} 계정을 ${COMMUNITY_SCOPE_LABELS[scope]}로 지정했습니다.` });
      await refreshScopedAdmins();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '관리자 지정에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const runRevokeScopedAdmin = async (admin) => {
    const key = `revoke:${admin.user_id}`;
    setWorking(key);
    setNotice({ type: '', message: '' });
    try {
      await revokeScopedPlatformAdmin({ targetUserId: admin.user_id });
      setNotice({ type: 'success', message: `${accountTitle(admin.account || {})} 계정의 관리자 지정을 해제했습니다.` });
      await refreshScopedAdmins();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '관리자 지정 해제에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const runIdentityUnban = async (ban) => {
    setWorking(`unban:${ban.id}`);
    setNotice({ type: '', message: '' });
    try {
      await unbanPlatformIdentity({ banId: ban.id, reason: unbanReason });
      setNotice({ type: 'success', message: `${IDENTITY_LABELS[ban.identity_type] || '식별자'} 밴을 해제했습니다.` });
      await refreshCurrent();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '식별자 밴 해제에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  return (
    <section className={`platform-account-moderation ${mobile ? 'mobile' : 'desktop'}`} aria-label="플랫폼 계정 관리">
      <header>
        <div>
          <h2>회원 탈퇴·식별자 밴</h2>
          <p>계정 UUID, DC 식별코드, 아카라이브 닉네임, 게임 UID 또는 닉네임으로 검색합니다.</p>
        </div>
      </header>

      <form className="platform-account-search" onSubmit={performSearch}>
        <label>
          <span>계정 검색</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="DC 식별코드, 게임 UID 또는 닉네임 입력"
            maxLength={512}
          />
        </label>
        <button type="submit" disabled={loading || Boolean(working)}>{loading ? '검색 중…' : '검색'}</button>
      </form>

      {searched && !loading && accounts.length === 0 && activeMatchingBans.length === 0 ? (
        <div className="platform-account-empty">
          <strong>일치하는 계정이나 밴 없음</strong>
          <p>아래 직접 식별자 밴에서 신규 가입을 미리 차단할 수 있습니다.</p>
        </div>
      ) : null}

      <div className="platform-account-results">
        {accounts.map((account) => {
          const form = forms[account.userId] || defaultAccountForm();
          const actionBusy = working.endsWith(account.userId);
          const activeBans = (account.bans || []).filter((ban) => ban.active);
          return (
            <article className="platform-account-card" key={account.userId}>
              <div className="platform-account-card-heading">
                <div>
                  <span>{account.activeBanned ? 'BANNED ACCOUNT' : 'ACCOUNT'}</span>
                  <h3>{accountTitle(account)}</h3>
                </div>
                <b className={account.activeBanned ? 'banned' : 'active'}>
                  {account.protectedPlatformAdmin ? '플랫폼 관리자' : account.activeBanned ? '밴 상태' : account.profile?.account_status || '확인됨'}
                </b>
              </div>

              <dl>
                <div><dt>계정 UUID</dt><dd>{account.userId}</dd></div>
                <div><dt>DC 식별코드</dt><dd>{account.dcGallog?.gallog_id || '-'}</dd></div>
                <div><dt>아카라이브</dt><dd>{account.arca?.display_name || account.arca?.profile_path || '-'}</dd></div>
                <div><dt>게임 UID</dt><dd>{account.gameProfile?.normalized_openid || '-'}</dd></div>
                <div><dt>게임 닉네임</dt><dd>{account.gameProfile?.nickname || '-'}</dd></div>
                <div><dt>유니온</dt><dd>{account.gameProfile?.guild_name || account.membership?.unions?.name || '-'}</dd></div>
                <div><dt>최근 로그인</dt><dd>{formatDate(account.auth?.lastSignInAt)}</dd></div>
              </dl>

              {activeBans.length ? (
                <div className="platform-account-ban-list">
                  {activeBans.map((ban) => (
                    <span key={ban.id}>{IDENTITY_LABELS[ban.identity_type] || ban.identity_type}: {ban.identity_value}</span>
                  ))}
                </div>
              ) : null}

              {!account.protectedPlatformAdmin ? (
                <div className="platform-account-scope-admin">
                  <label>
                    <span>갤길·챈길 관리자 지정</span>
                    <select
                      value={scopeSelections[account.userId] || 'gallery'}
                      onChange={(event) => setScopeSelections((current) => ({ ...current, [account.userId]: event.target.value }))}
                    >
                      <option value="gallery">갤러리(갤길) 관리자</option>
                      <option value="channel">채널(챈길) 관리자</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={working === `grant:${account.userId}` || Boolean(working && working !== `grant:${account.userId}`)}
                    onClick={() => runGrantScopedAdmin(account)}
                  >
                    {working === `grant:${account.userId}` ? '지정 중…' : '이 계정으로 지정'}
                  </button>
                </div>
              ) : null}

              {account.protectedPlatformAdmin ? (
                <p className="platform-account-protected">플랫폼 관리자 계정은 이 화면에서 밴하거나 삭제할 수 없습니다.</p>
              ) : (
                <div className="platform-account-action-form">
                  <label>
                    <span>처리 사유</span>
                    <input
                      type="text"
                      maxLength={500}
                      value={form.reason}
                      onChange={(event) => updateForm(account.userId, 'reason', event.target.value)}
                      placeholder="2자 이상 입력"
                    />
                  </label>
                  <label>
                    <span>확인 문구</span>
                    <input
                      type="text"
                      value={form.confirmation}
                      onChange={(event) => updateForm(account.userId, 'confirmation', event.target.value)}
                      placeholder="밴: 계정 밴 / 삭제: 계정 삭제"
                    />
                  </label>
                  <label className="platform-account-preserve-ban">
                    <input
                      type="checkbox"
                      checked={form.preserveBan}
                      onChange={(event) => updateForm(account.userId, 'preserveBan', event.target.checked)}
                    />
                    <span>계정 삭제 후 연결 식별자 재가입 차단 유지</span>
                  </label>
                  <div className="platform-account-actions">
                    <button
                      className="ban"
                      type="button"
                      disabled={actionBusy || Boolean(working && !actionBusy)}
                      onClick={() => runAccountAction(account, 'ban')}
                    >{working === `ban:${account.userId}` ? '처리 중…' : '계정·식별자 밴'}</button>
                    <button
                      className="unban"
                      type="button"
                      disabled={actionBusy || Boolean(working && !actionBusy) || !account.activeBanned}
                      onClick={() => runAccountAction(account, 'unban')}
                    >{working === `unban:${account.userId}` ? '처리 중…' : '밴 해제'}</button>
                    <button
                      className="delete"
                      type="button"
                      disabled={actionBusy || Boolean(working && !actionBusy)}
                      onClick={() => runAccountAction(account, 'delete')}
                    >{working === `delete:${account.userId}` ? '삭제 중…' : '계정 완전 삭제'}</button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <section className="platform-identity-ban-panel">
        <header>
          <div>
            <strong>직접 식별자 밴</strong>
            <p>아직 가입하지 않은 DC 식별코드·아카라이브 계정·게임 UID도 미리 차단합니다.</p>
          </div>
        </header>
        <form onSubmit={runManualBan}>
          <label>
            <span>종류</span>
            <select
              value={manual.identityType}
              onChange={(event) => setManual((current) => ({ ...current, identityType: event.target.value }))}
            >
              <option value="dc_gallog">DC 식별코드</option>
              <option value="arca">아카라이브 닉네임</option>
              <option value="game_openid">게임 UID</option>
            </select>
          </label>
          <label>
            <span>식별자</span>
            <input
              value={manual.identityValue}
              onChange={(event) => setManual((current) => ({ ...current, identityValue: event.target.value }))}
              placeholder="차단할 값"
              maxLength={512}
            />
          </label>
          <label>
            <span>밴 사유</span>
            <input
              value={manual.reason}
              onChange={(event) => setManual((current) => ({ ...current, reason: event.target.value }))}
              placeholder="2자 이상 입력"
              maxLength={500}
            />
          </label>
          <button type="submit" disabled={Boolean(working)}>{working === 'manual-ban' ? '처리 중…' : '식별자 밴'}</button>
        </form>
      </section>

      {activeMatchingBans.length ? (
        <section className="platform-matching-bans">
          <header>
            <strong>검색된 활성 밴</strong>
            <label>
              <span>해제 사유</span>
              <input value={unbanReason} maxLength={500} onChange={(event) => setUnbanReason(event.target.value)} />
            </label>
          </header>
          <div>
            {activeMatchingBans.map((ban) => (
              <article key={ban.id}>
                <div>
                  <strong>{IDENTITY_LABELS[ban.identity_type] || ban.identity_type}</strong>
                  <span>{ban.identity_value}</span>
                  <small>{ban.reason || '사유 없음'} · {formatDate(ban.created_at)}</small>
                </div>
                <button type="button" disabled={Boolean(working)} onClick={() => runIdentityUnban(ban)}>
                  {working === `unban:${ban.id}` ? '해제 중…' : '밴 해제'}
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="platform-scoped-admin-list">
        <header>
          <strong>지정된 갤길·챈길 관리자</strong>
          <p>커뮤니티 유형별 관리자는 해당 유형의 유니온 심사·관리·채팅 검수만 할 수 있습니다.</p>
        </header>
        {scopedAdmins.length ? (
          <div>
            {scopedAdmins.map((admin) => (
              <article key={admin.user_id}>
                <div>
                  <strong>{accountTitle(admin.account || {})}</strong>
                  <span>{COMMUNITY_SCOPE_LABELS[admin.scope] || admin.scope}</span>
                </div>
                <button
                  type="button"
                  disabled={working === `revoke:${admin.user_id}` || Boolean(working && working !== `revoke:${admin.user_id}`)}
                  onClick={() => runRevokeScopedAdmin(admin)}
                >
                  {working === `revoke:${admin.user_id}` ? '해제 중…' : '지정 해제'}
                </button>
              </article>
            ))}
          </div>
        ) : <p>지정된 갤길·챈길 관리자가 없습니다.</p>}
      </section>

      <details className="platform-moderation-history">
        <summary>최근 처리 이력 <span>{history.length}</span></summary>
        <div>
          {history.length ? history.map((item) => (
            <article key={item.id}>
              <strong>{ACTION_LABELS[item.action] || item.action}</strong>
              <span>{moderationTargetLabel(item)}</span>
              <p>{item.reason || '사유 없음'}</p>
              <small>{formatDate(item.created_at)}</small>
            </article>
          )) : <p>처리 이력이 없습니다.</p>}
        </div>
      </details>

      {notice.message ? <p className={`platform-account-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
